import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

export default function SignIn() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [removed, setRemoved] = useState(null);
  const [busy, setBusy] = useState(false);

  const go = (data) => {
    setUser(data);
    navigate(data.status === "approved" ? loc.state?.from || "/connect" : "/status", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return setError("Enter your email and password");
    setBusy(true);
    setError(""); setRemoved(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      go(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "self_removed") setRemoved(detail); else setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const reactivate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/reactivate", { email, password }); go(data); }
    catch (err) { setError(apiError(err)); setRemoved(null); } finally { setBusy(false); }
  };

  useEffect(() => {
    const token = new URLSearchParams(loc.hash.replace(/^#/, "")).get("reactivate");
    if (!token) return;
    window.history.replaceState(null, "", loc.pathname);
    setBusy(true);
    api.post("/auth/reactivate-link", { token }).then(({ data }) => { toast.success("Welcome back. Your account is active again."); go(data); })
      .catch((err) => setError(apiError(err))).finally(() => setBusy(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthLayout testId="signin-page" headline="Welcome back to your circle.">
      <h1 className="font-display text-5xl leading-[0.95] sm:text-6xl">Sign in</h1>
      <p className="mt-4 text-muted-foreground">Members and applicants sign in here.</p>
      <form onSubmit={submit} noValidate className="mt-10 space-y-6">
        <div>
          <label htmlFor="si-email" className="field-label">Email address</label>
          <input id="si-email" type="email" autoComplete="email" className="field" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signin-email-input" />
        </div>
        <div>
          <div className="flex items-baseline justify-between gap-4">
            <label htmlFor="si-password" className="field-label">Password</label>
            <Link to="/forgot-password" className="text-sm text-primary underline underline-offset-4" data-testid="signin-forgot-link">Forgot password?</Link>
          </div>
          <input id="si-password" type="password" autoComplete="current-password" className="field" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signin-password-input" />
        </div>
        {error && <p role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm" data-testid="signin-error">{error}</p>}
        {removed && (
          <div role="alert" className="space-y-3 rounded-lg border border-primary/40 bg-primary/10 p-4 text-sm" data-testid="signin-reactivate">
            <p>{removed.msg}</p>
            <button type="button" className="btn-primary min-h-[44px]" disabled={busy} onClick={reactivate} data-testid="signin-reactivate-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reactivate my account"}
            </button>
          </div>
        )}
        <button type="submit" className="btn-primary min-h-[56px] w-full text-lg" disabled={busy} data-testid="signin-submit-button">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
        </button>
        <div className="hairline" />
        <p className="text-center text-sm text-muted-foreground">Not a member yet? <Link to="/apply" className="font-semibold text-primary underline underline-offset-4" data-testid="signin-apply-link">Apply for membership</Link></p>
      </form>
    </AuthLayout>
  );
}
